import SEO from "@/components/SEO";

const sections = [
  {
    title: "Information we collect",
    content: (
      <>
        <p>We collect information that you provide to us and information generated when you use Nityavastra, including:</p>
        <ul>
          <li><strong>Account and profile data,</strong> such as your name, email address, sign-in information, and profile details. If you choose a third-party sign-in option, we receive the profile information you authorize that service to share with us.</li>
          <li><strong>Contact and delivery details,</strong> such as your recipient name, email address, delivery address, and other information needed to fulfil an order.</li>
          <li><strong>Order information,</strong> including the products you purchase, order status, returns, refunds, and transaction history.</li>
          <li><strong>Payment-related information.</strong> Payments are processed by payment service providers. Nityavastra may receive transaction references, payment status, and limited billing details, but payment card or bank credentials are handled by those providers.</li>
          <li><strong>Support communications,</strong> including messages, enquiries, feedback, and records of our correspondence with you.</li>
          <li><strong>Device and usage data,</strong> such as browser type, device information, IP address, pages visited, interactions, referring pages, and approximate location derived from technical data.</li>
          <li><strong>Cookies and similar technologies</strong> used to remember preferences, maintain sessions, understand site use, and support site functionality.</li>
        </ul>
      </>
    ),
  },
  {
    title: "How we use information",
    content: (
      <>
        <p>We use personal information to operate and improve Nityavastra, including to:</p>
        <ul>
          <li>create, secure, and manage customer accounts;</li>
          <li>process orders, payments, returns, and refunds;</li>
          <li>arrange delivery and provide order updates;</li>
          <li>respond to enquiries and provide customer support;</li>
          <li>detect, investigate, and prevent fraud, misuse, and security incidents;</li>
          <li>maintain our services, analyse performance, and improve the shopping experience;</li>
          <li>meet applicable record-keeping, tax, regulatory, and other legal requirements; and</li>
          <li>send promotions or marketing messages only where permitted and in line with your choices.</li>
        </ul>
      </>
    ),
  },
  {
    title: "When we share information",
    content: (
      <>
        <p>We share personal information only where it is reasonably needed to provide our services or meet an obligation. Recipients may include:</p>
        <ul>
          <li><strong>Delivery partners</strong> that need recipient and shipping details to deliver an order.</li>
          <li><strong>Payment service providers</strong> that process transactions, help verify payments, and manage payment-related issues.</li>
          <li><strong>Technology and service providers</strong> that support hosting, authentication, communications, analytics, customer support, security, and other business operations.</li>
          <li><strong>Authorities or other parties where legally required,</strong> or where necessary to respond to lawful requests, protect rights and safety, or investigate suspected fraud or misuse.</li>
        </ul>
        <p>We do not share personal information with third parties for their own marketing unless you have permitted it.</p>
      </>
    ),
  },
  {
    title: "Data security",
    content: (
      <p>We use reasonable administrative, technical, and organisational safeguards designed to protect personal information from unauthorised access, alteration, disclosure, or loss. No online service or storage system is completely secure, so we also encourage you to use a strong password and protect access to your account.</p>
    ),
  },
  {
    title: "Data retention",
    content: (
      <p>We retain personal information for as long as it is needed for the purposes described in this policy, including providing accounts and orders, resolving disputes, preventing fraud, maintaining business and transaction records, and meeting legal or regulatory requirements. Retention periods vary depending on the type of information and why it is held. We delete or anonymise information when it is no longer reasonably needed.</p>
    ),
  },
  {
    title: "Your choices and requests",
    content: (
      <>
        <p>You may review or update certain account details through your account. You may also contact us to request access to, correction of, or deletion of your personal information. We may need to verify your identity before completing a request, and some information may be retained where it is needed for an active order, security, record-keeping, or legal obligations.</p>
        <p>You can opt out of promotional emails by using the unsubscribe option in the message or by contacting us. Essential service messages, such as order or account notices, may still be sent.</p>
      </>
    ),
  },
  {
    title: "Cookies",
    content: (
      <p>Cookies and similar technologies help keep the website working, remember selections, maintain sign-in and cart sessions, measure site usage, and improve performance. You can accept or reject optional analytics using the choice shown when you visit, and change it later through Cookie preferences in the footer. Essential browser storage supports sign-in and site functions. You can also block or delete storage in your browser settings; doing so may affect site functionality.</p>
    ),
  },
  {
    title: "Children's privacy",
    content: (
      <p>Nityavastra is intended for a general audience and is not directed to children. We do not knowingly collect personal information from children without appropriate permission. If you believe a child has provided personal information to us, please contact us so we can review and address the request.</p>
    ),
  },
  {
    title: "Changes to this policy",
    content: (
      <p>We may update this Privacy Policy as our services or practices change. The latest version will be posted on this page with a revised effective date. We encourage you to review this page periodically.</p>
    ),
  },
];

export default function PrivacyPolicy() {
  return (
    <div className="relative px-4 pb-16 pt-[132px] sm:px-8 md:pb-24 md:pt-[148px]">
      <SEO
        title="Privacy Policy"
        description="Learn what personal information Nityavastra collects, how it is used and shared, and the choices available to you."
      />

      <div className="mx-auto max-w-4xl">
        <header className="border-b border-[#7C1F30]/15 pb-8 sm:pb-10">
          <p className="mb-3 text-xs uppercase tracking-[0.3em] text-[#7C1F30]">Your privacy</p>
          <h1 className="font-serif text-4xl leading-tight text-[#1C1917] sm:text-5xl md:text-6xl">Privacy Policy</h1>
          <p className="mt-5 max-w-2xl text-base leading-relaxed text-[#1C1917]/70 sm:text-lg">
            This policy explains how Nityavastra collects, uses, shares, and protects personal information when you visit our website, create an account, or shop with us.
          </p>
          <p className="mt-5 text-sm font-medium text-[#7C1F30]">Effective date: September 15, 2026</p>
        </header>

        <main className="mt-9 space-y-5 sm:mt-12 sm:space-y-6">
          {sections.map((section, index) => (
            <section
              key={section.title}
              className="rounded-2xl border border-[#E7E5E4] bg-white/90 p-5 shadow-[0_8px_30px_rgba(28,25,23,0.04)] sm:p-7"
            >
              <div className="flex items-start gap-4 sm:gap-5">
                <span className="mt-1 flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-[#7C1F30]/10 text-xs font-semibold text-[#7C1F30]">
                  {String(index + 1).padStart(2, "0")}
                </span>
                <div className="min-w-0">
                  <h2 className="font-serif text-2xl text-[#1C1917] sm:text-3xl">{section.title}</h2>
                  <div className="mt-3 space-y-3 text-sm leading-7 text-[#1C1917]/75 sm:text-base [&_li]:mb-2 [&_li]:pl-1 [&_strong]:font-semibold [&_strong]:text-[#1C1917] [&_ul]:list-disc [&_ul]:space-y-1 [&_ul]:pl-5">
                    {section.content}
                  </div>
                </div>
              </div>
            </section>
          ))}

          <section className="rounded-2xl bg-[#7C1F30] p-6 text-[#FAF3E7] sm:p-8">
            <p className="mb-2 text-xs uppercase tracking-[0.25em] text-[#D4A03A]">Contact us</p>
            <h2 className="font-serif text-2xl sm:text-3xl">Questions or privacy requests?</h2>
            <p className="mt-3 max-w-2xl text-sm leading-7 text-[#FAF3E7]/80 sm:text-base">
              For questions about this policy or requests concerning your personal information, email us at{" "}
              <a className="font-medium text-white underline decoration-[#D4A03A] underline-offset-4 hover:text-[#D4A03A]" href="mailto:admin@nityavastra.com">
                admin@nityavastra.com
              </a>.
            </p>
          </section>
        </main>
      </div>
    </div>
  );
}
